import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { parseCommitResourceId, parsePullResourceId } from './pull-resource-id.util';

import { MainService } from 'src/app/app.service';
import { SmartReviewApiService } from '../core/api/smart-review-api.service';
import { PublishCommentsResult, Review } from './interfaces/review.interface';
import { ReviewFile } from './interfaces/review-file.interface';
import { ReviewAnswer, ReviewAnswersResponse } from './interfaces/review-answer.interface';
import { ReviewFeedback, ReviewFeedbackListResponse, ReviewFeedbackRequest } from './interfaces/review-feedback.interface';
import { JiraIssuesResponse, SmartReviewPromptType } from '../core/interfaces/job.interface';
import { FAILED_STATUSES } from './review-constants';
import { Period } from '../core/utils/period.util';
import {
  PromptGenerationRequest,
  PromptGenerationResponse,
  SmartReviewAssistantJobResponse,
  TechnicalDocAnswer
} from './interfaces/technical-doc.interface';

export interface ReviewFilters {
  resourceTypes: string[];
  /** Origens do código revisado; vira ?sourceProvider= repetido. */
  sourceProviders: string[];
  statuses: string[];
  resourceId?: string;
  compareFrom: string;
  compareTo: string;
  requestedBy: string;
  /** Recorte pela data do pedido ("Solicitado em"); vira ?dateFrom=&dateTo= (inclusivos). */
  period?: Period;
  quickSearch: string;
}

export type ReviewSortBy = 'requestedAt' | 'lastRequestedAt' | 'lastActivityAt' | 'status' | 'requestedBy';

interface RawReviewItem {
  resourceId: string;
  resourceType?: string;
  from?: number | string;
  to?: number | string;
  areaId?: string;
  requestId?: string;
  versionFingerprint?: string;
  statusDerived?: string;
  status?: string;
  currentStep?: string;
  step?: string;
  requestedBy?: string;
  requestedAt?: string;
  lastRequestedAt?: string;
  lastActivityAt?: string;
  links?: { status?: string };
  jobs?: {
    review?: { id?: string; status?: string; attempts?: number; statusUrl?: string; createdAt?: string; updatedAt?: string; lastRetryBy?: string; lastRetryAt?: string; currentStep?: string; error?: string; promptType?: string };
    assistants?: Array<{ id?: string; status?: string; attempts?: number; statusUrl?: string; createdAt?: string; updatedAt?: string; lastRetryBy?: string; lastRetryAt?: string; error?: string; promptType?: string }>;
  };
  sourceName?: string;
  sourceDisplayName?: string;
  sourceProvider?: string;
  issueKey?: string;
  issueSummary?: string;
  issueStatus?: string;
}

interface ReviewsApiResponse {
  items?: RawReviewItem[];
  total?: number;
  limit?: number;
  offset?: number;
}

/**
 * O que acompanha a lista de arquivos além dos arquivos.
 *
 * `state` é a situação do Pull Request, e `sourceBranch` a branch de onde ele vem; na
 * comparação git, `repositorySlug` e o par from/to. Junto com a revisão de cada arquivo
 * (`ReviewFile.revision`), é o que permite à tela conferir se o arquivo mudou na origem
 * depois do review.
 */
export interface ReviewFilesMeta {
  state: string;
  sourceBranch?: string;
  repositorySlug?: string;
  fromRevision?: string;
  toRevision?: string;
}

interface FilesApiResponse {
  files?: Array<{ path?: string; filePath?: string; name?: string; fileName?: string; extension?: string; language?: string; lang?: string; revision?: string; contextOnly?: boolean }>;
}

export interface DiffExportResponse {
  diff: string;
  fromChangeset: number | null;
  toChangeset: number | null;
  contentDisposition: string | null;
}

@Injectable()
export class ReviewService extends SmartReviewApiService {
  private readonly endpointMap: Record<string, string> = {
    comparison: 'comparisons',
    standalone: 'standalones',
    issue: 'issues',
    changeset: 'changesets',
    pull: 'pulls',
    commit: 'commits',
    // A comparação git tem família de rota própria. Enquanto não tinha, o backend
    // colapsava o tipo em `comparison` para a tela poder endereçá-la — e o detalhe abria
    // no componente do TFVC, que rotula as versões como changeset e pede os arquivos numa
    // rota cujo handler resolve caminho por changeset_files, respondendo 404.
    'git-comparison': 'git-comparisons'
  };

  constructor(http: HttpClient, mainService: MainService) {
    super(http, mainService);
  }

  private normalizeResourceType(resourceType?: string): string {
    const value = (resourceType || '').trim().toLowerCase();
    if (!value) { return value; }

    const aliases: Record<string, string> = {
      compare: 'comparison',
      'comparison-upload': 'comparison',
      'standalone-upload': 'standalone',
    };

    return aliases[value] ?? value;
  }

  resolveEndpoint(resourceType: string): string {
    const normalizedType = this.normalizeResourceType(resourceType);
    return this.endpointMap[normalizedType] ?? 'issues';
  }

  /**
   * Monta o trecho de caminho que endereça o recurso, já com o identificador.
   *
   * Existe porque o Pull Request não cabe no padrão `{endpoint}/{resourceId}`: o
   * resourceId dele é `pr:<slug>:<número>` e a rota da API tem DOIS segmentos —
   * `/pulls/<slug>/<número>`. Concatenar como os demais produziria
   * `/pulls/pr:protheus:1/...`, que não existe.
   *
   * Um resourceId de Pull Request malformado cai no caminho genérico em vez de gerar
   * uma URL meio montada: o erro vira um 404 do backend, que é diagnosticável, em vez
   * de uma rota inventada.
   */
  resolveResourcePath(resourceType: string, resourceId: string): string {
    const endpoint = this.resolveEndpoint(resourceType);

    if (this.normalizeResourceType(resourceType) === 'pull') {
      const pull = parsePullResourceId(resourceId);
      if (pull) {
        return `${endpoint}/${encodeURIComponent(pull.slug)}/${pull.number}`;
      }
    }

    // O commit endereça igual: dois segmentos, slug e revisão.
    if (this.normalizeResourceType(resourceType) === 'commit') {
      const commit = parseCommitResourceId(resourceId);
      if (commit) {
        return `${endpoint}/${encodeURIComponent(commit.slug)}/${encodeURIComponent(commit.revision)}`;
      }
    }

    return `${endpoint}/${encodeURIComponent(resourceId)}`;
  }

  private toFiniteNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') { return undefined; }
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  }

  private extractComparisonRange(resourceId: string): { from?: number; to?: number } {
    const parts = (resourceId || '').split('_');
    if (parts.length < 2) {
      return {};
    }

    const from = this.toFiniteNumber(parts[0]);
    const to = this.toFiniteNumber(parts[1]);
    if (from === undefined || to === undefined) {
      return {};
    }
    return { from, to };
  }

  private canFetchFiles(status?: string, step?: string, allowWhenUnknown = true, reviewJobStatus?: string): boolean {
    const normalizedReviewJobStatus = (reviewJobStatus || '').trim().toLowerCase();
    const normalizedStatus = (status || '').trim().toLowerCase();
    const normalizedStep = (step || '').trim().toLowerCase();

    // Regra principal: etapa "review" concluída garante arquivos disponíveis. Com erro
    // também busca: o review pode ter parado no meio com parte dos arquivos gravada, e é
    // isso que quem tem visão global abre para ver (ver reviewOpenBlock). Sem esta busca a
    // tela ficava sem arquivo e pedia o diff do review inteiro, sem `path`.
    if (normalizedReviewJobStatus) {
      return normalizedReviewJobStatus === 'done' || FAILED_STATUSES.includes(normalizedReviewJobStatus);
    }

    if (!normalizedStatus && !normalizedStep) { return allowWhenUnknown; }
    return normalizedStatus === 'done' || normalizedStep === 'complete' || FAILED_STATUSES.includes(normalizedStatus);
  }

  /** Review que terminou com erro: pode não ter arquivo nenhum, e isso não é falha da busca. */
  private isFailedContext(status?: string, reviewJobStatus?: string): boolean {
    return FAILED_STATUSES.includes((reviewJobStatus || '').trim().toLowerCase())
      || FAILED_STATUSES.includes((status || '').trim().toLowerCase());
  }

  // ─── Leitura / Listagem ─────────────────────────────────────────────────────

  getReviews(
    filters?: ReviewFilters,
    limit = 50,
    offset = 0,
    sortBy: ReviewSortBy = 'requestedAt',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Observable<{ items: Review[]; total: number; limit: number; offset: number }> {
    let params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString())
      .set('sortBy', sortBy)
      .set('sortOrder', sortOrder);

    const resourceId = filters?.quickSearch?.trim();

    if (resourceId) {
      params = params.set('resourceId', resourceId);
      if (filters?.resourceTypes && filters.resourceTypes.length > 0) {
        filters.resourceTypes.forEach((type: string) => {
          params = params.append('resourceType', type);
        });
      }
    } else {
      // Sem resourceId: resourceTypes múltiplos são permitidos
      if (filters?.resourceTypes && filters.resourceTypes.length > 0) {
        filters.resourceTypes.forEach((type: string) => {
          params = params.append('resourceType', type);
        });
      }
      // compareFrom/compareTo só sem resourceId (contrato proíbe a combinação)
      if (filters?.compareFrom) {
        params = params.set('compareFrom', filters.compareFrom);
      }
      if (filters?.compareTo) {
        params = params.set('compareTo', filters.compareTo);
      }
    }

    if (filters?.sourceProviders && filters.sourceProviders.length > 0) {
      filters.sourceProviders.forEach((sourceProvider: string) => {
        params = params.append('sourceProvider', sourceProvider);
      });
    }

    // Filtros de status
    if (filters?.statuses && filters.statuses.length > 0) {
      filters.statuses.forEach((status: string) => {
        params = params.append('status', status);
      });
    }

    if (filters?.requestedBy) {
      params = params.set('requestedBy', filters.requestedBy);
    }

    if (filters?.period?.dateFrom) {
      params = params.set('dateFrom', filters.period.dateFrom);
    }
    if (filters?.period?.dateTo) {
      params = params.set('dateTo', filters.period.dateTo);
    }

    return this.http
      .get<ReviewsApiResponse | RawReviewItem[]>(`${this.apiURL}/reviews`, { headers: this.jsonHeaders, params })
      .pipe(
        map(res => {
          const apiRes: ReviewsApiResponse = Array.isArray(res) ? { items: res } : res as ReviewsApiResponse;
          const items: RawReviewItem[] = apiRes?.items ?? [];
          const total = apiRes?.total ?? items.length;

          const mappedItems = items.map(item => {
            const normalizedResourceType = this.normalizeResourceType(item.resourceType);
            const statusDerived = item.statusDerived ?? item.status ?? item.jobs?.review?.status;
            const currentStep = item.currentStep ?? item.step ?? item.jobs?.review?.currentStep;
            const parsedRange = this.extractComparisonRange(item.resourceId);
            const from = this.toFiniteNumber(item.from) ?? parsedRange.from;
            const to = this.toFiniteNumber(item.to) ?? parsedRange.to;

            return {
              resourceId: item.resourceId,
              resourceType: normalizedResourceType,
              from,
              to,
              areaId: item.areaId,
              requestId: item.requestId,
              versionFingerprint: item.versionFingerprint,
              statusDerived,
              currentStep,
              requestedBy: item.requestedBy,
              requestedAt: item.requestedAt,
              lastRequestedAt: item.lastRequestedAt,
              lastActivityAt: item.lastActivityAt,
              links: item.links,
              jobs: item.jobs,
              sourceName: item.sourceName,
              sourceDisplayName: item.sourceDisplayName,
              sourceProvider: item.sourceProvider,
              issueKey: item.issueKey,
              issueSummary: item.issueSummary,
              issueStatus: item.issueStatus,
            } as Review;
          });

          return {
            items: mappedItems,
            total,
            limit: apiRes?.limit ?? limit,
            offset: apiRes?.offset ?? offset
          };
        }),
        catchError(this.mainService.handleError)
      );
  }

  /**
   * Arquivos revisados.
   *
   * `onMeta` recebe o que vem na mesma resposta além dos arquivos (ver `ReviewFilesMeta`). É
   * callback e não retorno porque o tipo é consumido por vários chamadores que não têm o que fazer
   * com ela — é o mesmo idioma do `notify` dos serviços de combo, onde o contrato do
   * retorno também não comporta a informação extra.
   */
  getFiles(
    resourceType: string,
    resourceId: string,
    status?: string,
    step?: string,
    reviewJobStatus?: string,
    onMeta?: (meta: ReviewFilesMeta) => void
  ): Observable<ReviewFile[]> {
    if (!this.canFetchFiles(status, step, true, reviewJobStatus)) {
      return of([]);
    }

    const hasStatusContext = !!((status || '').trim() || (step || '').trim());
    const resourcePath = this.resolveResourcePath(resourceType, resourceId);
    return this.http
      .get<FilesApiResponse | FilesApiResponse['files']>(`${this.apiURL}/${resourcePath}/files`, {
        headers: this.jsonHeaders
      })
      .pipe(
        map(res => {
          const files = Array.isArray(res) ? res : (res as FilesApiResponse)?.files ?? [];
          onMeta?.(this.extractFilesMeta(res));
          return files.map(f => ({
            path: f.path || f.filePath || f.name || '',
            name: f.name || f.fileName || (f.path ? f.path.split('/').pop() : '') || '',
            extension: f.extension || '',
            language: f.language || f.lang || '',
            ...(f.revision ? { revision: f.revision } : {}),
            ...(f.contextOnly ? { contextOnly: true } : {})
          })) as ReviewFile[];
        }),
        catchError((error: HttpErrorResponse) => {
          // O 403 segue como veio: handleError troca por um Error genérico e o status some,
          // e sem ele a tela não distingue "sem acesso" de "sem arquivos".
          if (error.status === 403) {
            return throwError(() => error);
          }
          if (error.status === 404) {
            if (hasStatusContext && !this.isFailedContext(status, reviewJobStatus)) {
              return this.mainService.handleError(error);
            }
            return of([] as ReviewFile[]);
          }
          return this.mainService.handleError(error);
        })
      );
  }

  private extractFilesMeta(res: unknown): ReviewFilesMeta {
    if (Array.isArray(res) || !res || typeof res !== 'object') { return { state: '' }; }
    const raw = res as Record<string, unknown>;
    const text = (key: string) => (typeof raw[key] === 'string' ? (raw[key] as string).trim() : '') || undefined;
    return {
      state: text('state') ?? '',
      sourceBranch: text('sourceBranch'),
      repositorySlug: text('repositorySlug'),
      fromRevision: text('fromRevision'),
      toRevision: text('toRevision')
    };
  }

  getDiff(resourceType: string, resourceId: string, filePath?: string): Observable<DiffExportResponse> {
    const resourcePath = this.resolveResourcePath(resourceType, resourceId);
    let params = new HttpParams()
      .set('format', 'diff')
      .set('diff_full_context', 'true')
      .set('download', 'true');
    // Recursos com mais de um arquivo precisam dizer qual: issue, changeset e pull
    // request. Comparison e standalone têm um arquivo só e a API o resolve sozinha.
    const typesThatNeedPath = ['issue', 'changeset', 'pull', 'commit'];
    if (filePath && typesThatNeedPath.includes(this.normalizeResourceType(resourceType))) {
      params = params.set('path', filePath);
    }

    return this.http.get(`${this.apiURL}/${resourcePath}/diff/export`, {
      headers: new HttpHeaders({
        Authorization: this.jsonHeaders.get('Authorization') || '',
        Accept: 'text/plain'
      }),
      params,
      observe: 'response',
      responseType: 'text' as const
    }).pipe(
      map(response => ({
        diff: response.body ?? '',
        fromChangeset: this.parseChangesetHeader(response.headers.get('X-From-Changeset')),
        toChangeset: this.parseChangesetHeader(response.headers.get('X-To-Changeset')),
        contentDisposition: response.headers.get('Content-Disposition')
      }))
    );
  }

  private parseChangesetHeader(value: string | null): number | null {
    const raw = (value ?? '').trim();
    if (!raw) { return null; }
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed < 0) { return null; }
    return parsed;
  }

  getAnswers<TAnswer = ReviewAnswer>(
    resourceType: string,
    resourceId: string,
    promptType: SmartReviewPromptType = 'code_review'
  ): Observable<ReviewAnswersResponse<TAnswer> | null> {
    const resourcePath = this.resolveResourcePath(resourceType, resourceId);
    const params = new HttpParams().set('promptType', promptType);

    return this.http
      .get<ReviewAnswersResponse<TAnswer>>(`${this.apiURL}/${resourcePath}/answers`, {
        headers: this.jsonHeaders,
        params
      })
      // 403 passa adiante: é a API dizendo que quem está logado não pode abrir o review
      // (repositório, TFVC ou recorte da área), e a tela precisa do motivo. Virar null
      // mostraria "ainda não passou pelo assistente" num review que tem análise.
      .pipe(catchError((error: HttpErrorResponse) => error?.status === 403 ? throwError(() => error) : of(null)));
  }

  getTechnicalDocAnswers(resourceType: string, resourceId: string): Observable<ReviewAnswersResponse<TechnicalDocAnswer> | null> {
    return this.getAnswers<TechnicalDocAnswer>(resourceType, resourceId, 'technical_doc');
  }

  // ─── Feedback de Reviews ────────────────────────────────────────────────────

  /** Endpoint de feedback usa o caminho literal reviews/{resourceType} (não as coleções plurais). */
  private feedbackBaseUrl(resourceType: string, resourceId: string): string {
    return `${this.apiURL}/reviews/${this.normalizeResourceType(resourceType)}/${resourceId}/feedback`;
  }

  getReviewFeedback(resourceType: string, resourceId: string): Observable<ReviewFeedbackListResponse> {
    return this.http
      .get<ReviewFeedbackListResponse>(this.feedbackBaseUrl(resourceType, resourceId), {
        headers: this.jsonHeaders
      })
      .pipe(catchError(this.mainService.handleError));
  }

  // Sem handleError aqui: ele descarta o HttpErrorResponse e relança um Error
  // genérico, perdendo o status e a `message` do backend. Deixamos o erro
  // original propagar para o componente exibir a mensagem da API (ex.: 400 de
  // "Comentário muito longo (máximo de 1000 caracteres)").
  saveReviewFeedback(
    resourceType: string,
    resourceId: string,
    body: ReviewFeedbackRequest
  ): Observable<ReviewFeedback> {
    return this.http.post<ReviewFeedback>(this.feedbackBaseUrl(resourceType, resourceId), body, {
      headers: this.jsonHeaders
    });
  }

  deleteReviewFeedback(resourceType: string, resourceId: string, feedbackId: string): Observable<void> {
    return this.http.delete<void>(`${this.feedbackBaseUrl(resourceType, resourceId)}/${feedbackId}`, {
      headers: this.jsonHeaders
    });
  }

  generatePrompt(
    resourceType: string,
    resourceId: string,
    request: PromptGenerationRequest
  ): Observable<PromptGenerationResponse> {
    const resourcePath = this.resolveResourcePath(resourceType, resourceId);
    return this.http
      .post<PromptGenerationResponse>(
        `${this.apiURL}/${resourcePath}/prompts`,
        request,
        { headers: this.jsonHeaders }
      )
      .pipe(catchError(this.mainService.handleError));
  }

  runAssistant(
    resourceType: string,
    resourceId: string,
    promptType: SmartReviewPromptType,
    includeSource = true
  ): Observable<SmartReviewAssistantJobResponse> {
    const resourcePath = this.resolveResourcePath(resourceType, resourceId);
    const body = { includeSource, promptType };
    return this.http
      .post<SmartReviewAssistantJobResponse>(
        `${this.apiURL}/${resourcePath}/assistant`,
        body,
        { headers: this.jsonHeaders }
      )
      .pipe(catchError(this.mainService.handleError));
  }

  getSource(resourceType: string, resourceId: string, filePath: string): Observable<string> {
    const resourcePath = this.resolveResourcePath(resourceType, resourceId);
    const params = new HttpParams().set('path', filePath).set('raw', 'true');

    return this.http.get(`${this.apiURL}/${resourcePath}/source`, {
      headers: new HttpHeaders({
        Authorization: this.jsonHeaders.get('Authorization') || '',
        Accept: 'text/plain'
      }),
      params,
      responseType: 'text' as const
    });
  }

  // ─── Exclusão de Reviews ──────────────────────────────────────────────────

  /**
   * Recebe o CAMINHO já montado, e não o endpoint: o Pull Request endereça por slug e
   * número, e concatenar o resourceId aqui devolveria a rota errada.
   */
  private deleteReviewByPath(resourcePath: string, requestedBy?: string): Observable<void> {
    let params = new HttpParams();
    if (requestedBy?.trim()) {
      params = params.set('requestedBy', requestedBy.trim());
    }
    return this.http.delete<void>(`${this.apiURL}/${resourcePath}`, {
      headers: this.jsonHeaders,
      params
    });
  }

  deleteChangesetReview(resourceId: string, requestedBy?: string): Observable<void> {
    return this.deleteReviewByPath(this.resolveResourcePath('changeset', resourceId), requestedBy);
  }

  deleteIssueReview(resourceId: string, requestedBy?: string): Observable<void> {
    return this.deleteReviewByPath(this.resolveResourcePath('issue', resourceId), requestedBy);
  }

  deleteComparisonReview(resourceId: string, requestedBy?: string): Observable<void> {
    return this.deleteReviewByPath(this.resolveResourcePath('comparison', resourceId), requestedBy);
  }

  deleteGitComparisonReview(resourceId: string, requestedBy?: string): Observable<void> {
    return this.deleteReviewByPath(this.resolveResourcePath('git-comparison', resourceId), requestedBy);
  }

  deleteStandaloneReview(resourceId: string, requestedBy?: string): Observable<void> {
    return this.deleteReviewByPath(this.resolveResourcePath('standalone', resourceId), requestedBy);
  }

  deletePullRequestReview(resourceId: string, requestedBy?: string): Observable<void> {
    return this.deleteReviewByPath(this.resolveResourcePath('pull', resourceId), requestedBy);
  }

  deleteCommitReview(resourceId: string, requestedBy?: string): Observable<void> {
    return this.deleteReviewByPath(this.resolveResourcePath('commit', resourceId), requestedBy);
  }

  deleteReview(resourceType: Review['resourceType'], resourceId: string, requestedBy?: string): Observable<void> {
    const normalizedType = this.normalizeResourceType(resourceType) as Review['resourceType'];

    switch (normalizedType) {
      case 'changeset':
        return this.deleteChangesetReview(resourceId, requestedBy);
      case 'comparison':
        return this.deleteComparisonReview(resourceId, requestedBy);
      case 'git-comparison':
        return this.deleteGitComparisonReview(resourceId, requestedBy);
      case 'standalone':
        return this.deleteStandaloneReview(resourceId, requestedBy);
      case 'pull':
        return this.deletePullRequestReview(resourceId, requestedBy);
      case 'commit':
        return this.deleteCommitReview(resourceId, requestedBy);
      case 'issue':
        return this.deleteIssueReview(resourceId, requestedBy);
    }

    // Tipo desconhecido falha aqui, e não caindo em issue. O fallback anterior mandava
    // o resourceId de um Pull Request para DELETE /issues/{key}: a rota existe, então a
    // API não devolvia 404 — procurava a participação do usuário num review de issue que
    // não existe e respondia "review não solicitado por você". O dono do review lia que
    // o review não era dele.
    return throwError(() => new Error(`Tipo de review não suportado para exclusão: ${resourceType}`));
  }


  // Sem `startAt`: a rota não pagina (só lê `maxResults`), e o combo já filtra em memória
  // sobre uma única página. Um parâmetro que o backend nunca lê insinua paginação que não
  // existe.
  getMyJiraIssues(maxResults = 20): Observable<JiraIssuesResponse> {
    const params = new HttpParams().set('maxResults', maxResults.toString());

    return this.http
      .get<JiraIssuesResponse>(`${this.apiURL}/issues/jira/mine`, {
        headers: this.jsonHeaders,
        params
      })
      .pipe(catchError(this.mainService.handleError));
  }

  // ─── TFVC ───────────────────────────────────────────────────────────────────
  // ─── Envio de Reviews ───────────────────────────────────────────────────────

  /**
   * Comparison: arquivo local vs versão TFVC (changeset from → 0).
   * Multipart/form-data: path, file, parâmetros de IA.
   */
  submitComparisonWithFile(
    fromChangeset: string,
    tfvcPath: string,
    file: File,
    issueKey = ''
  ): Observable<{ jobId: string }> {
    const formData = new FormData();
    formData.append('path', tfvcPath);
    formData.append('file', file, file.name);
    formData.append('runAssistant', 'true');
    formData.append('runAssistantIncludeSource', 'true');
    // Campos separados, e não um `inline` com JSON: no multipart desta rota o backend só
    // lê `inlineMaxLines`/`inlineBaseRadius` como campos de form (o `inline` estruturado é
    // exclusivo do corpo JSON, usado no modo changeset×changeset). Um `inline` aqui é campo
    // desconhecido — o Go descarta em silêncio — e o diff completo (0/0) nunca chegava:
    // a comparação caía nos padrões (5000 linhas / raio 80), truncando o contexto do review.
    formData.append('inlineMaxLines', '0');
    formData.append('inlineBaseRadius', '0');
    formData.append('promptType', 'code_review');
    // Só vai quando existe: um campo vazio no multipart chegaria como string vazia e
    // seria indistinguível de "informei e está em branco".
    const normalizedIssueKey = this.normalizeOptionalIssueKey(issueKey);
    if (normalizedIssueKey) {
      formData.append('issueKey', normalizedIssueKey);
    }

    return this.http
      .post<{ jobId: string }>(
        `${this.apiURL}/comparisons/${fromChangeset}/0/process`,
        formData,
        { headers: this.authHeaders }
      );
  }

  /**
   * Git comparison: revisão do repositório vs arquivo local enviado.
   */
  submitGitComparisonCommitToFile(
    repoSlug: string,
    path: string,
    fromRevision: string,
    file: File,
    issueKey = ''
  ): Observable<{ jobId: string }> {
    const formData = new FormData();
    formData.append('path', path.trim().replace(/^\/+/, ''));
    formData.append('fromRevision', fromRevision);
    formData.append('file', file, file.name);
    formData.append('runAssistant', 'true');
    formData.append('includeSource', 'true');
    formData.append('promptType', 'code_review');
    const normalizedIssueKey = this.normalizeOptionalIssueKey(issueKey);
    if (normalizedIssueKey) {
      formData.append('issueKey', normalizedIssueKey);
    }

    return this.http
      .post<{ jobId: string }>(
        `${this.apiURL}/git-comparisons/${encodeURIComponent(repoSlug)}/process`,
        formData,
        { headers: this.authHeaders }
      );
  }

  /**
   * Git comparison: duas revisões do mesmo arquivo no repositório.
   */
  submitGitComparisonCommitToCommit(
    repoSlug: string,
    path: string,
    fromRevision: string,
    toRevision: string,
    issueKey = ''
  ): Observable<{ jobId: string }> {
    const body = {
      path: path.trim().replace(/^\/+/, ''),
      fromRevision,
      toRevision,
      runAssistant: true,
      includeSource: true,
      promptType: 'code_review'
    } as Record<string, unknown>;
    const normalizedIssueKey = this.normalizeOptionalIssueKey(issueKey);
    if (normalizedIssueKey) {
      body['issueKey'] = normalizedIssueKey;
    }

    return this.http
      .post<{ jobId: string }>(
        `${this.apiURL}/git-comparisons/${encodeURIComponent(repoSlug)}/process`,
        body,
        { headers: this.jsonHeaders }
      );
  }

  /**
   * Comparison: changeset de origem vs changeset de destino no TFVC (JSON body).
   */
  submitComparisonTfvcToTfvc(
    fromChangeset: string,
    toChangeset: string,
    tfvcPath: string,
    issueKey = ''
  ): Observable<{ jobId: string }> {
    const body: Record<string, unknown> = {
      path: tfvcPath,
      inline: { baseRadius: 0, maxLines: 0 },
      promptType: 'code_review',
      runAssistant: true,
      runAssistantIncludeSource: true
    };
    const normalizedIssueKey = this.normalizeOptionalIssueKey(issueKey);
    if (normalizedIssueKey) {
      body['issueKey'] = normalizedIssueKey;
    }
    return this.http
      .post<{ jobId: string }>(
        `${this.apiURL}/comparisons/${fromChangeset}/${toChangeset}/process`,
        body,
        { headers: this.jsonHeaders }
      );
  }

  /**
   * Standalone: análise de arquivo isolado (multipart/form-data).
   */
  submitStandalone(file: File, issueKey = ''): Observable<{ jobId: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('runAssistant', 'true');
    const normalizedIssueKey = this.normalizeOptionalIssueKey(issueKey);
    if (normalizedIssueKey) {
      formData.append('issueKey', normalizedIssueKey);
    }

    return this.http
      .post<{ jobId: string }>(
        `${this.apiURL}/standalones/process`,
        formData,
        { headers: this.authHeaders }
      );
  }

  /**
   * Issue: solicitar review de uma issue Jira (JSON body).
   */
  submitIssue(issueKey: string): Observable<{ jobId: string }> {
    const body = {
      inline: { baseRadius: 0, maxLines: 0 },
      runAssistant: true,
      runAssistantIncludeSource: true
    };
    return this.http
      .post<{ jobId: string }>(
        `${this.apiURL}/issues/${issueKey}/process`,
        body,
        { headers: this.jsonHeaders }
      );
  }

  /**
   * Issue: revisão incremental (delta) — processa apenas changesets novos.
   * Retorna httpStatus 200 quando não há changesets novos, 202 quando um job foi criado.
   */
  submitIssueDelta(issueKey: string): Observable<{ jobId?: string; message?: string; httpStatus: number }> {
    const body = { runAssistant: true, runAssistantIncludeSource: true, detectDelta: true };
    return this.http
      .post<{ jobId?: string; message?: string }>(
        `${this.apiURL}/issues/${issueKey}/process`,
        body,
        { headers: this.jsonHeaders, observe: 'response' }
      )
      .pipe(
        map(res => ({ ...res.body, httpStatus: res.status } as { jobId?: string; message?: string; httpStatus: number }))
      );
  }

  /**
   * Changeset: solicitar review completo de um changeset pelo ID (JSON body).
   */
  /**
   * Pede o review de um Pull Request.
   *
   * A rota é endereçada por slug e número, e não pelo resourceId: o backend registra o
   * repositório já precisa estar cadastrado, e o slug é o identificador público que a
   * navegação devolve pronto.
   *
   * `postComments` fica de fora de propósito. Publicar o achado no PR escreve no
   * repositório do time, e essa é uma decisão de produto — não um padrão que a tela
   * assume por conta própria.
   */
  submitPullRequestReview(repoSlug: string, number: number, issueKey = ''): Observable<{ jobId: string }> {
    return this.http
      .post<{ jobId: string }>(
        `${this.apiURL}/pulls/${encodeURIComponent(repoSlug)}/${number}/process`,
        this.gitReviewBody(issueKey),
        { headers: this.jsonHeaders }
      );
  }

  /**
   * Corpo comum aos pedidos de review de repositório git.
   *
   * O cadastro do repositório não entra aqui. Ele é pré-condição operacional e fica nas
   * rotas admin, para impedir que qualquer repo visível no provedor vire suportado por
   * acidente.
   */
  private gitReviewBody(issueKey = ''): Record<string, unknown> {
    const body: Record<string, unknown> = {
      promptType: 'code_review',
      runAssistant: true,
      // `includeSource`, e não `runAssistantIncludeSource`: as rotas git nomeiam o campo
      // assim. O nome longo é das rotas do TFVC, e o Go descarta campo desconhecido em
      // silêncio — com ele, o fonte completo simplesmente não ia ao prompt, sem erro
      // nenhum. A comparação git deste mesmo arquivo já usava o nome certo.
      includeSource: true,
      postComments: false
    };
    const normalizedIssueKey = this.normalizeOptionalIssueKey(issueKey);
    if (normalizedIssueKey) {
      body['issueKey'] = normalizedIssueKey;
    }
    return body;
  }

  private normalizeOptionalIssueKey(issueKey: string): string {
    return String(issueKey || '').trim().toUpperCase();
  }

  /**
   * Publica no diff do Pull Request os achados de um review já apurado.
   *
   * Rota separada do processamento de propósito: publicar não custa um review novo, e a
   * decisão acontece DEPOIS de ler o resultado. A operação é idempotente — chamar de
   * novo devolve tudo como `skipped`, sem duplicar comentário no PR.
   */
  publishPullRequestComments(resourceId: string, promptType: SmartReviewPromptType = 'code_review'): Observable<PublishCommentsResult> {
    const path = this.resolveResourcePath('pull', resourceId);
    return this.http.post<PublishCommentsResult>(
      `${this.apiURL}/${path}/comments`,
      {},
      { headers: this.jsonHeaders, params: new HttpParams().set('promptType', promptType) }
    );
  }

  /**
   * Dispara o review de um commit avulso — o análogo do changeset no fluxo TFVC.
   *
   * Sem postComments: a devolutiva inline pertence ao Pull Request, que tem discussão.
   * Um commit já está no histórico e não tem onde receber comentário.
   */
  submitCommitReview(repoSlug: string, revision: string, issueKey = ''): Observable<{ jobId: string }> {
    const body = this.gitReviewBody(issueKey);
    // Commit não recebe comentário inline: já está no histórico e não tem onde.
    delete body['postComments'];
    return this.http
      .post<{ jobId: string }>(
        `${this.apiURL}/commits/${encodeURIComponent(repoSlug)}/${encodeURIComponent(revision)}/process`,
        body,
        { headers: this.jsonHeaders }
      );
  }

  submitChangesetReview(changesetId: string, issueKey = ''): Observable<{ jobId: string }> {
    const body: Record<string, unknown> = {
      inline: { baseRadius: 0, maxLines: 0 },
      runAssistant: true,
      runAssistantIncludeSource: true
    };
    const normalizedIssueKey = this.normalizeOptionalIssueKey(issueKey);
    if (normalizedIssueKey) {
      body['issueKey'] = normalizedIssueKey;
    }
    return this.http
      .post<{ jobId: string }>(
        `${this.apiURL}/changesets/${changesetId}/process`,
        body,
        { headers: this.jsonHeaders }
      );
  }

  // ─── Jobs ───────────────────────────────────────────────────────────────────

  getReviewStatus(resourceId: string, resourceType: string): Observable<Review | null> {
    const filters: ReviewFilters = {
      resourceTypes: [resourceType],
      sourceProviders: [],
      statuses: [],
      resourceId,
      compareFrom: '',
      compareTo: '',
      requestedBy: '',
      quickSearch: resourceId
    };
    return this.getReviews(filters, 1, 0).pipe(
      map(res => res.items[0] ?? null),
      catchError(() => of(null))
    );
  }

}
